import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete, Edit, Send } from '@mui/icons-material';
import { ITaskComment, taskCommentSchema } from '../Interfaces/ITask';
import { MentionTextField } from '../generic/MentionTextField';
import { useGetUsers } from '../hooks/data';
import { castWithZeroTimezone } from '../core/generic_helper';

interface IProps {
  commentArray: ITaskComment[];
  setCommentArray: (comments: ITaskComment[]) => void;
  /** Geschlossene Aufgabe -> nur lesen. */
  disableEdit?: boolean;
  /** Lazy-Load im Vorgang läuft noch. */
  isLoading?: boolean;
}

/**
 * Kommentar-Verlauf einer Aufgabe (#1190): gestapelte Liste (neueste zuerst,
 * analog Vorgangs-Notizen), eigene Kommentare dezent hervorgehoben; @-Mentions
 * über MentionTextField, Bearbeiten/Löschen nur eigener Kommentare.
 * Arbeitet rein lokal auf commentArray — persistiert wird beim Speichern der
 * Aufgabe bzw. des Vorgangs; Benachrichtigung und Log übernimmt das Backend.
 */
export const TaskComments: React.FC<IProps> = ({
  commentArray,
  setCommentArray,
  disableEdit = false,
  isLoading = false,
}) => {
  const { users } = useGetUsers();
  const idUser = Number(localStorage.getItem('idUser'));

  const [newComment, setNewComment] = useState('');
  const [newMentions, setNewMentions] = useState<number[]>([]);
  const [editId, setEditId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [editMentions, setEditMentions] = useState<number[]>([]);

  const handleAdd = () => {
    if (newComment.trim() === '') {
      return;
    }
    let tmpId = -1;
    if (commentArray.length > 0) {
      const minId = Math.min(...commentArray.map(c => c.idTaskComment));
      if (minId <= tmpId) {
        tmpId = minId - 1;
      }
    }
    const currentUser = users?.find(user => user.idUser === idUser);
    setCommentArray([
      ...commentArray,
      {
        ...taskCommentSchema.parse({}),
        idTaskComment: tmpId,
        idUser,
        Comment: newComment,
        MentionedUserIds: newMentions,
        FirstName: currentUser?.FirstName ?? '',
        LastName: currentUser?.LastName ?? '',
        Created_at: new Date().toISOString(),
      },
    ]);
    setNewComment('');
    setNewMentions([]);
  };

  const handleStartEdit = (comment: ITaskComment) => {
    setEditId(comment.idTaskComment);
    setEditText(comment.Comment);
    setEditMentions(comment.MentionedUserIds);
  };

  const handleSaveEdit = () => {
    setCommentArray(
      commentArray.map(comment =>
        comment.idTaskComment === editId
          ? {
              ...comment,
              Comment: editText,
              MentionedUserIds: editMentions,
              isEdited: comment.idTaskComment > 0 ? true : comment.isEdited,
            }
          : comment
      )
    );
    setEditId(null);
  };

  const handleRemove = (idTaskComment: number) => {
    setCommentArray(
      commentArray.filter(comment => comment.idTaskComment !== idTaskComment)
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Neueste zuerst — wie bei den Vorgangs-Notizen.
  const sortedComments = [...commentArray].sort(
    (a, b) =>
      new Date(b.Created_at).getTime() - new Date(a.Created_at).getTime()
  );

  return (
    <Box>
      <Box sx={{ maxHeight: '45vh', overflowY: 'auto', pr: 1 }}>
        {sortedComments.length === 0 && (
          <Typography variant='body2' color='text.secondary' sx={{ my: 2 }}>
            Noch keine Kommentare
          </Typography>
        )}
        {sortedComments.map(comment => {
          const isOwn = comment.idUser === idUser;
          return (
            <Paper
              key={`task-comment-${comment.idTaskComment}`}
              elevation={1}
              sx={{
                p: 1.5,
                mb: 1.5,
                borderRadius: 2,
                // Eigene Kommentare dezent hervorheben (kein Chat-Layout).
                borderLeft: '4px solid',
                borderLeftColor: isOwn ? 'primary.main' : 'transparent',
                backgroundColor: isOwn
                  ? 'rgba(25, 118, 210, 0.06)'
                  : 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography variant='caption' fontWeight={600}>
                  {isOwn
                    ? 'Ich'
                    : `${comment.FirstName ?? ''} ${comment.LastName ?? ''}`}
                </Typography>
                {isOwn && !disableEdit && editId !== comment.idTaskComment && (
                  <Box>
                    <Tooltip title='Kommentar bearbeiten' arrow>
                      <IconButton
                        size='small'
                        onClick={() => handleStartEdit(comment)}
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Kommentar löschen' arrow>
                      <IconButton
                        size='small'
                        onClick={() => handleRemove(comment.idTaskComment)}
                      >
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              {editId === comment.idTaskComment ? (
                <Box sx={{ mt: 1 }}>
                  <MentionTextField
                    value={editText}
                    mentionedUserIds={editMentions}
                    onChange={(value, mentions) => {
                      setEditText(value);
                      setEditMentions(mentions);
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <Button size='small' onClick={() => setEditId(null)}>
                      Abbruch
                    </Button>
                    <Button
                      size='small'
                      variant='contained'
                      onClick={handleSaveEdit}
                    >
                      Übernehmen
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Typography
                  variant='body2'
                  sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                >
                  {comment.Comment}
                </Typography>
              )}

              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mt: 0.5 }}
              >
                {castWithZeroTimezone(
                  String(comment.Created_at)
                ).toLocaleString()}
                {comment.isEdited ? ' · (Bearbeitet)' : ''}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      {!disableEdit && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2 }}>
          <MentionTextField
            value={newComment}
            mentionedUserIds={newMentions}
            onChange={(value, mentions) => {
              setNewComment(value);
              setNewMentions(mentions);
            }}
            placeholder='Kommentar schreiben — mit @ Kollegen erwähnen…'
            suggestionsPosition='top'
          />
          <Tooltip title='Kommentar hinzufügen' arrow>
            <span>
              <IconButton
                color='primary'
                onClick={handleAdd}
                disabled={newComment.trim() === ''}
              >
                <Send />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
      {!disableEdit && (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', mt: 0.5 }}
        >
          Kommentare werden mit dem Speichern der Aufgabe übernommen; erwähnte
          Kollegen werden dann benachrichtigt.
        </Typography>
      )}
    </Box>
  );
};
